// Admin configuration screen for configurable work-order attributes.
// Admins define the identity fields their work orders carry (e.g. Job Order #,
// VIN for a vehicle upfitter; Batch #, Lot Code for a pharma customer), choose
// where each renders on an MO card (headline / identifier / chip / detail),
// and can start from a built-in industry template.
// Not routed yet - the parent mounts it where appropriate.
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
	Alert,
	Box,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Snackbar,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
	AttributeDataType,
	AttributeDisplaySlot,
	AttributeTemplate,
	parseEnumOptions,
	UpdateWorkOrderAttributeDefinitionDto, // eslint-disable-line sort-imports
	WorkOrderAttributeDefinition,
	workOrderAttributesApi,
} from "../../../services/work-order-attributes-api";
import {
	EmptyState,
	GhostButton,
	MonoText,
	planner,
	PlannerCard, // eslint-disable-line sort-imports
	PlannerHeader,
	PlannerPage,
	PrimaryButton,
	SectionLabel,
} from "../planner-ui";

// ---- Design-system styling (see planner-ui/tokens.ts) ----

// Every control in this screen: hairline rest state, small radius, brick focus.
const fieldSx = {
	bgcolor: planner.surface,
	"& .MuiOutlinedInput-root": {
		fontFamily: planner.fontUi,
		borderRadius: `${planner.radiusSm}px`,
		"& fieldset": { borderColor: planner.hairlineStrong },
		"&:hover fieldset": { borderColor: planner.inkMuted },
		"&.Mui-focused fieldset": {
			borderColor: planner.accent,
			borderWidth: `${planner.borderWidth}px`,
		},
	},
	"& .MuiInputLabel-root": {
		fontFamily: planner.fontUi,
		color: planner.inkSecondary,
		"&.Mui-focused": { color: planner.accent },
	},
	"& .MuiFormHelperText-root": {
		...planner.type.meta,
		fontFamily: planner.fontUi,
		color: planner.inkMuted,
	},
} as const;

// Small pill used for slot / data-type / flag metadata on each row.
const metaPillSx = {
	...planner.type.metaStrong,
	fontFamily: planner.fontUi,
	px: 1,
	py: 0.25,
	borderRadius: `${planner.radiusSm}px`,
	whiteSpace: "nowrap" as const,
};

const dialogPaperSx = {
	borderRadius: `${planner.radius}px`,
	boxShadow: planner.shadowDialog,
	fontFamily: planner.fontUi,
};

const dialogTitleSx = {
	...planner.type.pageTitle,
	color: planner.navy,
	bgcolor: planner.surface,
};

const DATA_TYPES: { value: AttributeDataType; label: string }[] = [
	{ value: "text", label: "Text" },
	{ value: "number", label: "Number" },
	{ value: "date", label: "Date" },
	{ value: "enum", label: "Choice list" },
];

const DISPLAY_SLOTS: { value: AttributeDisplaySlot; label: string; hint: string }[] = [
	{ value: "headline", label: "Headline", hint: "Bold title of the MO card" },
	{ value: "identifier", label: "Identifier", hint: "Monospaced line under the headline (VINs, lot codes)" },
	{ value: "chip", label: "Chip", hint: "Small pill on the card" },
	{ value: "detail", label: "Detail", hint: "Quiet meta line at the bottom" },
];

interface EditorState {
	label: string;
	attrKey: string;
	dataType: AttributeDataType;
	enumOptionsText: string;
	validationRegex: string;
	isRequired: boolean;
	isSearchable: boolean;
	displaySlot: AttributeDisplaySlot;
}

const EMPTY_EDITOR: EditorState = {
	label: "",
	attrKey: "",
	dataType: "text",
	enumOptionsText: "",
	validationRegex: "",
	isRequired: false,
	isSearchable: true,
	displaySlot: "chip",
};

// "Job Order #" -> "jobOrderNo"-style camelCase key suggestion
const suggestAttrKey = (label: string): string => {
	const words = label
		.replace(/#/g, " no ")
		.replace(/[^a-zA-Z0-9 ]/g, " ")
		.trim()
		.split(/\s+/)
		.filter(Boolean);
	if (words.length === 0) return "";
	return words
		.map((word, index) =>
			index === 0
				? word.toLowerCase()
				: word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
		)
		.join("")
		.slice(0, 64);
};

// Sample value used by the live preview
const sampleValue = (definition: WorkOrderAttributeDefinition): string => {
	if (definition.dataType === "enum") {
		return parseEnumOptions(definition.enumOptions)[0] || "Option A";
	}
	if (definition.dataType === "number") return "128";
	if (definition.dataType === "date") return new Date().toISOString().slice(0, 10);
	if (/vin/i.test(definition.attrKey)) return "1FTBW3XK0PKA00001";
	if (definition.displaySlot === "headline") return "1000245";
	return `${definition.label.replace(/[#:]/g, "").trim()} 1042`;
};

const toUpdateDto = (
	definition: WorkOrderAttributeDefinition
): UpdateWorkOrderAttributeDefinitionDto => ({
	id: definition.id,
	companyId: definition.companyId,
	attrKey: definition.attrKey,
	label: definition.label,
	dataType: definition.dataType,
	enumOptions: definition.enumOptions,
	validationRegex: definition.validationRegex,
	isRequired: definition.isRequired,
	isSearchable: definition.isSearchable,
	displaySlot: definition.displaySlot,
	sortOrder: definition.sortOrder,
	isActive: definition.isActive,
});

// Live preview of how an MO card renders with the configured attributes.
// Mirrors the slot layout of the MO card on the Work Plan.
const MOCardPreview: React.FC<{ definitions: WorkOrderAttributeDefinition[] }> = ({ definitions }) => {
	const active = useMemo(
		() =>
			definitions
				.filter((definition) => definition.isActive)
				.sort((a, b) => a.sortOrder - b.sortOrder),
		[definitions]
	);
	const bySlot = useCallback(
		(slot: AttributeDisplaySlot) => active.filter((definition) => definition.displaySlot === slot),
		[active]
	);
	const headlineDefinition = bySlot("headline")[0];
	const identifiers = bySlot("identifier");
	const chips = bySlot("chip");
	const details = bySlot("detail");

	return (
		<Box
			sx={{
				// Same recipe as PlannerCard so the preview matches the real MO card:
				// 1.5px hairline, 6px radius, no resting shadow.
				bgcolor: planner.surface,
				border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
				borderRadius: `${planner.radius}px`,
				boxShadow: planner.shadowRest,
				fontFamily: planner.fontUi,
				p: 1.25,
				maxWidth: 260,
			}}
		>
			<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink, lineHeight: 1.3 }}>
				{headlineDefinition ? sampleValue(headlineDefinition) : "Untitled order"}
			</Typography>
			{identifiers.length > 0 && (
				<MonoText sx={{ display: "block", mt: 0.25, lineHeight: 1.4, wordBreak: "break-all" }}>
					{identifiers.map((definition) => sampleValue(definition)).join(" · ")}
				</MonoText>
			)}
			<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mt: 0.75 }}>
				<Box
					component="span"
					sx={{
						...metaPillSx,
						fontFamily: planner.fontMono,
						bgcolor: planner.surfaceSubtle,
						border: `1px solid ${planner.hairline}`,
						color: planner.inkSecondary,
						fontVariantNumeric: "tabular-nums",
					}}
				>
					07:00–15:30
				</Box>
				{chips.map((definition) => (
					<Box
						key={definition.attrKey}
						component="span"
						title={definition.label}
						sx={{
							...metaPillSx,
							bgcolor: planner.surfaceSubtle,
							border: `1px solid ${planner.hairline}`,
							color: planner.inkSecondary,
						}}
					>
						{sampleValue(definition)}
					</Box>
				))}
			</Box>
			{details.length > 0 && (
				<Typography sx={{ ...planner.type.meta, color: planner.inkMuted, mt: 0.5, lineHeight: 1.35 }}>
					{details
						.map((definition) => `${definition.label} ${sampleValue(definition)}`)
						.join(" · ")}
				</Typography>
			)}
		</Box>
	);
};

const AttributesAdmin: React.FC = () => {
	const companyId = useSelector((state: any) => state?.user?.userData?.companyId);

	const [definitions, setDefinitions] = useState<WorkOrderAttributeDefinition[]>([]);
	const [loading, setLoading] = useState(false);
	const [templates, setTemplates] = useState<AttributeTemplate[]>([]);
	const [showTemplatePicker, setShowTemplatePicker] = useState(false);
	const [applyingTemplateKey, setApplyingTemplateKey] = useState<string | null>(null);
	const [editorOpen, setEditorOpen] = useState(false);
	const [editingDefinition, setEditingDefinition] = useState<WorkOrderAttributeDefinition | null>(null);
	const [editor, setEditor] = useState<EditorState>(EMPTY_EDITOR);
	const [keyEdited, setKeyEdited] = useState(false);
	const [editorError, setEditorError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [deletingDefinition, setDeletingDefinition] = useState<WorkOrderAttributeDefinition | null>(null);
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity?: "success" | "error" }>({
		open: false,
		message: "",
	});

	const loadDefinitions = useCallback(async () => {
		if (!companyId) return;
		setLoading(true);
		try {
			const data = await workOrderAttributesApi.getByCompany(companyId);
			setDefinitions(data);
		} catch (error: any) {
			console.error("Failed to load work order attribute definitions:", error);
			setSnackbar({
				open: true,
				message: error?.response?.data?.message || "Failed to load attribute definitions",
				severity: "error",
			});
		} finally {
			setLoading(false);
		}
	}, [companyId]);

	useEffect(() => {
		loadDefinitions();
	}, [loadDefinitions]);

	const handleOpenTemplatePicker = useCallback(async () => {
		setShowTemplatePicker(true);
		if (templates.length === 0) {
			try {
				const data = await workOrderAttributesApi.getTemplates();
				setTemplates(data);
			} catch (error: any) {
				console.error("Failed to load attribute templates:", error);
				setSnackbar({
					open: true,
					message: error?.response?.data?.message || "Failed to load templates",
					severity: "error",
				});
			}
		}
	}, [templates.length]);

	const handleApplyTemplate = useCallback(async (templateKey: string) => {
		if (!companyId) return;
		setApplyingTemplateKey(templateKey);
		try {
			const result = await workOrderAttributesApi.applyTemplate(companyId, templateKey);
			setShowTemplatePicker(false);
			setSnackbar({
				open: true,
				message: `${result.message} — ${result.addedCount} added, ${result.skippedCount} already present`,
				severity: "success",
			});
			await loadDefinitions();
		} catch (error: any) {
			console.error("Failed to apply attribute template:", error);
			setSnackbar({
				open: true,
				message: error?.response?.data?.message || "Failed to apply template",
				severity: "error",
			});
		} finally {
			setApplyingTemplateKey(null);
		}
	}, [companyId, loadDefinitions]);

	const handleOpenCreate = useCallback(() => {
		setEditingDefinition(null);
		setEditor(EMPTY_EDITOR);
		setKeyEdited(false);
		setEditorError(null);
		setEditorOpen(true);
	}, []);

	const handleOpenEdit = useCallback((definition: WorkOrderAttributeDefinition) => {
		setEditingDefinition(definition);
		setEditor({
			label: definition.label,
			attrKey: definition.attrKey,
			dataType: definition.dataType,
			enumOptionsText: parseEnumOptions(definition.enumOptions).join(", "),
			validationRegex: definition.validationRegex || "",
			isRequired: definition.isRequired,
			isSearchable: definition.isSearchable,
			displaySlot: definition.displaySlot,
		});
		setKeyEdited(true);
		setEditorError(null);
		setEditorOpen(true);
	}, []);

	const handleSaveDefinition = useCallback(async () => {
		if (!companyId) return;
		const label = editor.label.trim();
		const attrKey = editor.attrKey.trim();
		if (!label || !attrKey) {
			setEditorError("Label and key are required");
			return;
		}
		if (!/^[a-zA-Z][a-zA-Z0-9]*$/.test(attrKey)) {
			setEditorError("Key must be alphanumeric and start with a letter (e.g. jobOrderNo)");
			return;
		}
		const enumOptions = editor.enumOptionsText
			.split(",")
			.map((option) => option.trim())
			.filter(Boolean);
		if (editor.dataType === "enum" && enumOptions.length === 0) {
			setEditorError("Choice list attributes need at least one option");
			return;
		}
		if (editor.validationRegex) {
			try {
				new RegExp(editor.validationRegex); // eslint-disable-line no-new
			} catch {
				setEditorError("Validation pattern is not a valid regular expression");
				return;
			}
		}

		setSaving(true);
		setEditorError(null);
		try {
			const dto = {
				companyId,
				attrKey,
				label,
				dataType: editor.dataType,
				enumOptions: editor.dataType === "enum" ? JSON.stringify(enumOptions) : undefined,
				validationRegex: editor.validationRegex.trim() || undefined,
				isRequired: editor.isRequired,
				isSearchable: editor.isSearchable,
				displaySlot: editor.displaySlot,
				sortOrder: editingDefinition
					? editingDefinition.sortOrder
					: (definitions.length > 0 ? Math.max(...definitions.map((d) => d.sortOrder)) : 0) + 1,
			};
			if (editingDefinition) {
				await workOrderAttributesApi.update(editingDefinition.id, {
					...dto,
					id: editingDefinition.id,
					isActive: editingDefinition.isActive,
				});
			} else {
				await workOrderAttributesApi.create(dto);
			}
			setEditorOpen(false);
			setSnackbar({
				open: true,
				message: editingDefinition ? "Attribute updated" : "Attribute added",
				severity: "success",
			});
			await loadDefinitions();
		} catch (error: any) {
			console.error("Failed to save attribute definition:", error);
			setEditorError(error?.response?.data?.message || "Failed to save attribute");
		} finally {
			setSaving(false);
		}
	}, [companyId, editor, editingDefinition, definitions, loadDefinitions]);

	const handleConfirmDelete = useCallback(async () => {
		if (!deletingDefinition) return;
		try {
			await workOrderAttributesApi.delete(deletingDefinition.id);
			setDefinitions((prev) => prev.filter((d) => d.id !== deletingDefinition.id));
			setSnackbar({ open: true, message: "Attribute deleted", severity: "success" });
		} catch (error: any) {
			console.error("Failed to delete attribute definition:", error);
			setSnackbar({
				open: true,
				message: error?.response?.data?.message || "Failed to delete attribute",
				severity: "error",
			});
		} finally {
			setDeletingDefinition(null);
		}
	}, [deletingDefinition]);

	// Reorder via up/down buttons: swap positions, renumber SortOrder 1..n,
	// persist only the rows whose SortOrder changed.
	const handleMove = useCallback(async (index: number, delta: -1 | 1) => {
		const target = index + delta;
		if (target < 0 || target >= definitions.length) return;
		const reordered = [...definitions];
		[reordered[index], reordered[target]] = [reordered[target], reordered[index]];
		const renumbered = reordered.map((definition, position) => ({
			...definition,
			sortOrder: position + 1,
		}));
		const changed = renumbered.filter((definition) => {
			const before = definitions.find((d) => d.id === definition.id);
			return before && before.sortOrder !== definition.sortOrder;
		});
		setDefinitions(renumbered);
		try {
			await Promise.all(
				changed.map((definition) =>
					workOrderAttributesApi.update(definition.id, toUpdateDto(definition))
				)
			);
		} catch (error: any) {
			console.error("Failed to reorder attribute definitions:", error);
			setSnackbar({
				open: true,
				message: error?.response?.data?.message || "Failed to save the new order",
				severity: "error",
			});
			await loadDefinitions();
		}
	}, [definitions, loadDefinitions]);

	const slotLabel = useCallback(
		(slot: AttributeDisplaySlot) =>
			DISPLAY_SLOTS.find((option) => option.value === slot)?.label || slot,
		[]
	);

	return (
		<PlannerPage>
			<PlannerHeader
				title="Work order attributes"
				subtitle="Define the identity fields your work orders carry and where each one shows on the card."
				actions={
					<>
						<GhostButton startIcon={<AutoAwesomeIcon />} onClick={handleOpenTemplatePicker}>
							Apply template
						</GhostButton>
						<PrimaryButton startIcon={<AddIcon />} onClick={handleOpenCreate}>
							Add attribute
						</PrimaryButton>
					</>
				}
			/>

			{loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
					<CircularProgress size={28} sx={{ color: planner.navy }} />
				</Box>
			) : (
				<Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start", flexWrap: "wrap" }}>
					<Box sx={{ flex: "1 1 480px", minWidth: 0 }}>
						{definitions.length === 0 ? (
							<PlannerCard>
								<EmptyState
									title="No attributes configured"
									body="Start from an industry template or add your first attribute. Work orders will pick these fields up in the Add / Edit form and on their cards."
									actionLabel="Apply a template"
									onAction={handleOpenTemplatePicker}
								/>
							</PlannerCard>
						) : (
							<PlannerCard>
								{definitions.map((definition, index) => (
									<Box
										key={definition.id}
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 1.5,
											px: 2,
											py: 1.25,
											borderTop: index > 0 ? `1px solid ${planner.hairline}` : "none",
											"&:hover .attr-row-actions": { opacity: 1 },
										}}
									>
										<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
											<IconButton
												size="small"
												disabled={index === 0}
												onClick={() => handleMove(index, -1)}
												sx={{ p: 0.25, color: planner.inkMuted }}
											>
												<ArrowUpwardIcon sx={{ fontSize: 14 }} />
											</IconButton>
											<IconButton
												size="small"
												disabled={index === definitions.length - 1}
												onClick={() => handleMove(index, 1)}
												sx={{ p: 0.25, color: planner.inkMuted }}
											>
												<ArrowDownwardIcon sx={{ fontSize: 14 }} />
											</IconButton>
										</Box>
										<Box sx={{ flex: 1, minWidth: 0 }}>
											<Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
												<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink }}>
													{definition.label}
												</Typography>
												<MonoText>{definition.attrKey}</MonoText>
											</Box>
											<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
												<Box
													component="span"
													sx={{
														...metaPillSx,
														bgcolor: planner.accentSoft,
														border: `1px solid ${planner.accentBorder}`,
														color: planner.accentDeep,
													}}
												>
													{slotLabel(definition.displaySlot)}
												</Box>
												<Box
													component="span"
													sx={{
														...metaPillSx,
														bgcolor: planner.surfaceSubtle,
														border: `1px solid ${planner.hairline}`,
														color: planner.inkSecondary,
													}}
												>
													{DATA_TYPES.find((type) => type.value === definition.dataType)?.label || definition.dataType}
												</Box>
												{definition.isRequired && (
													<Box
														component="span"
														sx={{
															...metaPillSx,
															bgcolor: planner.partialSoft,
															border: `1px solid ${planner.partial}33`,
															color: planner.partial,
														}}
													>
														Required
													</Box>
												)}
												{definition.validationRegex && (
													<Tooltip title={definition.validationRegex}>
														<Box
															component="span"
															sx={{
																...metaPillSx,
																fontFamily: planner.fontMono,
																bgcolor: planner.surfaceSubtle,
																border: `1px solid ${planner.hairline}`,
																color: planner.inkMuted,
															}}
														>
															Pattern
														</Box>
													</Tooltip>
												)}
											</Box>
										</Box>
										<Box
											className="attr-row-actions"
											sx={{ display: "flex", gap: 0.25, opacity: 0, transition: "opacity 120ms ease", flexShrink: 0 }}
										>
											<Tooltip title="Edit">
												<IconButton
													size="small"
													onClick={() => handleOpenEdit(definition)}
													sx={{ p: 0.5, color: planner.inkMuted, "&:hover": { color: planner.ink } }}
												>
													<EditOutlinedIcon sx={{ fontSize: 16 }} />
												</IconButton>
											</Tooltip>
											<Tooltip title="Delete">
												<IconButton
													size="small"
													onClick={() => setDeletingDefinition(definition)}
													sx={{ p: 0.5, color: planner.inkMuted, "&:hover": { color: planner.unstaffed } }}
												>
													<DeleteOutlineIcon sx={{ fontSize: 16 }} />
												</IconButton>
											</Tooltip>
										</Box>
									</Box>
								))}
							</PlannerCard>
						)}
					</Box>

					<Box sx={{ flex: "0 0 300px" }}>
						<SectionLabel sx={{ mb: 1 }}>Card preview</SectionLabel>
						<MOCardPreview definitions={definitions} />
						<Typography sx={{ ...planner.type.meta, color: planner.inkMuted, mt: 1, maxWidth: 260 }}>
							How a work order card on the Work Plan renders with these attributes, using sample values.
						</Typography>
					</Box>
				</Box>
			)}

			{/* Add / edit attribute dialog */}
			<Dialog
				open={editorOpen}
				onClose={() => setEditorOpen(false)}
				maxWidth="sm"
				fullWidth
				PaperProps={{ sx: dialogPaperSx }}
			>
				<DialogTitle sx={dialogTitleSx}>
					{editingDefinition ? "Edit attribute" : "Add attribute"}
					<IconButton
						onClick={() => setEditorOpen(false)}
						sx={{ position: "absolute", right: 8, top: 8, color: planner.inkMuted }}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers sx={{ bgcolor: planner.canvas, borderColor: planner.hairline }}>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
						{editorError && (
							<Alert
								severity="error"
								sx={{
									borderRadius: `${planner.radiusSm}px`,
									bgcolor: planner.unstaffedSoft,
									border: `1px solid ${planner.unstaffed}33`,
									color: planner.ink,
									"& .MuiAlert-icon": { color: planner.unstaffed },
									"& .MuiAlert-message": { ...planner.type.body, fontFamily: planner.fontUi },
								}}
							>
								{editorError}
							</Alert>
						)}
						<TextField
							label="Label"
							value={editor.label}
							onChange={(e) =>
								setEditor((prev) => ({
									...prev,
									label: e.target.value,
									attrKey: keyEdited ? prev.attrKey : suggestAttrKey(e.target.value),
								}))
							}
							placeholder="e.g. Job Order #"
							fullWidth
							sx={fieldSx}
						/>
						<TextField
							label="Key"
							value={editor.attrKey}
							onChange={(e) => {
								setKeyEdited(true);
								setEditor((prev) => ({ ...prev, attrKey: e.target.value }));
							}}
							placeholder="e.g. jobOrderNo"
							helperText="Stable identifier stored with each work order's values"
							fullWidth
							sx={fieldSx}
							InputProps={{ sx: { fontFamily: planner.fontMono, fontSize: 13 } }}
						/>
						<FormControl fullWidth sx={fieldSx}>
							<InputLabel>Data type</InputLabel>
							<Select
								value={editor.dataType}
								label="Data type"
								onChange={(e) =>
									setEditor((prev) => ({ ...prev, dataType: e.target.value as AttributeDataType }))
								}
							>
								{DATA_TYPES.map((type) => (
									<MenuItem key={type.value} value={type.value} sx={{ fontFamily: planner.fontUi, fontSize: 13 }}>
										{type.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						{editor.dataType === "enum" && (
							<TextField
								label="Options"
								value={editor.enumOptionsText}
								onChange={(e) => setEditor((prev) => ({ ...prev, enumOptionsText: e.target.value }))}
								placeholder="e.g. Granulation, Blending, Compression"
								helperText="Comma-separated list of allowed values"
								fullWidth
								sx={fieldSx}
							/>
						)}
						<FormControl fullWidth sx={fieldSx}>
							<InputLabel>Display slot</InputLabel>
							<Select
								value={editor.displaySlot}
								label="Display slot"
								onChange={(e) =>
									setEditor((prev) => ({ ...prev, displaySlot: e.target.value as AttributeDisplaySlot }))
								}
							>
								{DISPLAY_SLOTS.map((slot) => (
									<MenuItem key={slot.value} value={slot.value}>
										<Box>
											<Typography sx={{ ...planner.type.body, fontFamily: planner.fontUi, color: planner.ink }}>
												{slot.label}
											</Typography>
											<Typography sx={{ ...planner.type.meta, fontFamily: planner.fontUi, color: planner.inkMuted }}>
												{slot.hint}
											</Typography>
										</Box>
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<TextField
							label="Validation pattern (optional)"
							value={editor.validationRegex}
							onChange={(e) => setEditor((prev) => ({ ...prev, validationRegex: e.target.value }))}
							placeholder="e.g. ^[A-HJ-NPR-Z0-9]{17}$"
							helperText="Regular expression the value must match"
							fullWidth
							sx={fieldSx}
							InputProps={{ sx: { fontFamily: planner.fontMono, fontSize: 13 } }}
						/>
						<Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
							<FormControlLabel
								control={
									<Checkbox
										checked={editor.isRequired}
										onChange={(e) => setEditor((prev) => ({ ...prev, isRequired: e.target.checked }))}
										sx={{ color: planner.hairlineStrong, "&.Mui-checked": { color: planner.navy } }}
									/>
								}
								label="Required"
								sx={{ "& .MuiFormControlLabel-label": { ...planner.type.body, fontFamily: planner.fontUi, color: planner.ink } }}
							/>
							<FormControlLabel
								control={
									<Checkbox
										checked={editor.isSearchable}
										onChange={(e) => setEditor((prev) => ({ ...prev, isSearchable: e.target.checked }))}
										sx={{ color: planner.hairlineStrong, "&.Mui-checked": { color: planner.navy } }}
									/>
								}
								label="Searchable"
								sx={{ "& .MuiFormControlLabel-label": { ...planner.type.body, fontFamily: planner.fontUi, color: planner.ink } }}
							/>
						</Box>
					</Box>
				</DialogContent>
				<DialogActions sx={{ bgcolor: planner.surface }}>
					<GhostButton onClick={() => setEditorOpen(false)}>Cancel</GhostButton>
					<PrimaryButton onClick={handleSaveDefinition} disabled={saving}>
						{saving ? "Saving..." : editingDefinition ? "Update" : "Add"}
					</PrimaryButton>
				</DialogActions>
			</Dialog>

			{/* Template picker dialog */}
			<Dialog
				open={showTemplatePicker}
				onClose={() => setShowTemplatePicker(false)}
				maxWidth="sm"
				fullWidth
				PaperProps={{ sx: dialogPaperSx }}
			>
				<DialogTitle sx={dialogTitleSx}>
					Apply an industry template
					<IconButton
						onClick={() => setShowTemplatePicker(false)}
						sx={{ position: "absolute", right: 8, top: 8, color: planner.inkMuted }}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers sx={{ bgcolor: planner.canvas, borderColor: planner.hairline }}>
					{templates.length === 0 ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
							<CircularProgress size={24} sx={{ color: planner.navy }} />
						</Box>
					) : (
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
							<Typography sx={{ ...planner.type.body, fontFamily: planner.fontUi, color: planner.inkSecondary }}>
								Templates add their attributes to your configuration. Attributes you already have (matched by key) are left untouched.
							</Typography>
							{templates.map((template) => (
								<PlannerCard key={template.key} sx={{ p: 2 }}>
									<Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }}>
										<Box sx={{ minWidth: 0 }}>
											<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink }}>
												{template.name}
											</Typography>
											<Typography sx={{ ...planner.type.meta, color: planner.inkSecondary, mt: 0.25 }}>
												{template.description}
											</Typography>
											<Typography sx={{ ...planner.type.meta, color: planner.inkMuted, mt: 0.5 }}>
												{template.definitions.map((definition) => definition.label).join(" · ")}
											</Typography>
										</Box>
										<GhostButton
											size="small"
											onClick={() => handleApplyTemplate(template.key)}
											disabled={applyingTemplateKey !== null}
											sx={{ flexShrink: 0 }}
										>
											{applyingTemplateKey === template.key ? "Applying..." : "Apply"}
										</GhostButton>
									</Box>
								</PlannerCard>
							))}
						</Box>
					)}
				</DialogContent>
			</Dialog>

			{/* Delete confirmation dialog */}
			<Dialog
				open={Boolean(deletingDefinition)}
				onClose={() => setDeletingDefinition(null)}
				maxWidth="xs"
				fullWidth
				PaperProps={{ sx: dialogPaperSx }}
			>
				<DialogTitle sx={dialogTitleSx}>Delete attribute?</DialogTitle>
				<DialogContent sx={{ bgcolor: planner.surface }}>
					<Typography sx={{ ...planner.type.body, fontFamily: planner.fontUi, color: planner.inkSecondary }}>
						{`"${deletingDefinition?.label}" will be removed from the work order form and cards. Values already stored on work orders are kept but no longer shown.`}
					</Typography>
				</DialogContent>
				<DialogActions sx={{ bgcolor: planner.surface }}>
					<GhostButton onClick={() => setDeletingDefinition(null)}>Cancel</GhostButton>
					{/* Destructive confirm — danger status colour, not MUI's error palette.
					    The pressed state darkens the same token rather than adding a shade. */}
					<PrimaryButton
						onClick={handleConfirmDelete}
						sx={{
							bgcolor: planner.unstaffed,
							borderColor: planner.unstaffed,
							"&:hover": {
								bgcolor: planner.unstaffed,
								borderColor: planner.unstaffed,
								filter: "brightness(0.9)",
							},
						}}
					>
						Delete
					</PrimaryButton>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={5000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
			>
				<Alert
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					severity={snackbar.severity || "info"}
					sx={{
						width: "100%",
						borderRadius: `${planner.radiusSm}px`,
						fontFamily: planner.fontUi,
						boxShadow: planner.shadowHover,
						"& .MuiAlert-message": { ...planner.type.body, fontFamily: planner.fontUi },
						...(snackbar.severity === "error"
							? {
								bgcolor: planner.unstaffedSoft,
								border: `1px solid ${planner.unstaffed}33`,
								color: planner.ink,
								"& .MuiAlert-icon": { color: planner.unstaffed },
							}
							: {
								bgcolor: planner.staffedSoft,
								border: `1px solid ${planner.staffed}33`,
								color: planner.ink,
								"& .MuiAlert-icon": { color: planner.staffed },
							}),
					}}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</PlannerPage>
	);
};

export default AttributesAdmin;
